import React from 'react';
import styled from 'styled-components';

const PaginationContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`;

const PageButton = styled.button`
	padding: 8px 12px;
	min-width: 36px;
	height: 36px;
	border: 1px solid ${(props) => (props.active ? '#374151' : '#E5E7EB')};
	background-color: ${(props) => (props.active ? '#4B5563' : '#FFFFFF')};
	color: ${(props) => (props.active ? '#FFFFFF' : '#6B7280')};
	border-radius: 6px;
	cursor: pointer;
	font-size: 14px;
	font-weight: ${(props) => (props.active ? '600' : '500')};
	transition: all 0.2s ease;

	&:hover:not(:disabled) {
		background-color: ${(props) => (props.active ? '#374151' : '#F3F4F6')};
		border-color: ${(props) => (props.active ? '#374151' : '#D1D5DB')};
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
		background-color: #f9fafb;
		color: #d1d5db;
		border-color: #e5e7eb;
	}

	&.arrow {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
	}
`;

const PageInfo = styled.span`
	color: #6b7280;
	font-size: 14px;
	margin: 0 12px;
`;

const Ellipsis = styled.span`
	color: #9ca3af;
	font-size: 14px;
	padding: 0 8px;
`;

const Pagination = ({
	currentPage,
	totalPages,
	onPageChange,
	showPageInfo = true,
}) => {
	// 페이지 번호 범위 계산
	const getPageNumbers = () => {
		const delta = 2; // 현재 페이지 주변에 보여줄 페이지 수
		const range = [];
		const rangeWithDots = [];
		let l;

		for (let i = 1; i <= totalPages; i++) {
			if (
				i === 1 ||
				i === totalPages ||
				(i >= currentPage - delta && i <= currentPage + delta)
			) {
				range.push(i);
			}
		}

		range.forEach((i) => {
			if (l) {
				if (i - l === 2) {
					rangeWithDots.push(l + 1);
				} else if (i - l !== 1) {
					rangeWithDots.push('...');
				}
			}
			rangeWithDots.push(i);
			l = i;
		});

		return rangeWithDots;
	};

	const pageNumbers = getPageNumbers();

	const handlePageClick = (page) => {
		if (page !== currentPage && page >= 1 && page <= totalPages) {
			onPageChange(page);
		}
	};

	const handlePrevClick = () => {
		if (currentPage > 1) {
			onPageChange(currentPage - 1);
		}
	};

	const handleNextClick = () => {
		if (currentPage < totalPages) {
			onPageChange(currentPage + 1);
		}
	};

	// 페이지가 0개일 때만 숨김 (1개 이상은 모두 표시)
	if (totalPages < 1) {
		return null;
	}

	return (
		<PaginationContainer>
			{/* 이전 버튼 */}
			<PageButton
				className='arrow'
				onClick={handlePrevClick}
				disabled={currentPage === 1}>
				‹
			</PageButton>

			{/* 페이지 번호들 */}
			{pageNumbers.map((page, index) => {
				if (page === '...') {
					return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
				}
				return (
					<PageButton
						key={page}
						active={page === currentPage}
						onClick={() => handlePageClick(page)}>
						{page}
					</PageButton>
				);
			})}

			{/* 다음 버튼 */}
			<PageButton
				className='arrow'
				onClick={handleNextClick}
				disabled={currentPage === totalPages}>
				›
			</PageButton>

			{/* 페이지 정보 */}
			{showPageInfo && (
				<PageInfo>
					{currentPage} / {totalPages} 페이지
				</PageInfo>
			)}
		</PaginationContainer>
	);
};

export default Pagination;
